import { Link } from "react-router-dom";
import { ChevronRight, LucideIcon } from "lucide-react";
import { LiveClock } from "./LiveClock";

interface Crumb {
  label: string;
  to?: string;
}

interface Action {
  label: string;
  icon?: LucideIcon;
  onClick?: () => void;
  to?: string;
  variant?: "primary" | "secondary" | "ghost";
  badge?: number;
}

interface PageHeaderProps {
  /** Breadcrumbs: ej. [{label:"Inicio", to:"/tienda"}, {label:"Reservas"}] */
  crumbs: Crumb[];
  /** Ícono opcional junto al título */
  icon?: LucideIcon;
  /** Acciones del lado derecho */
  actions?: Action[];
  /** Mostrar reloj */
  clock?: boolean;
}

export function PageHeader({ crumbs, icon: Icon, actions = [], clock = true }: PageHeaderProps) {
  const title = crumbs[crumbs.length - 1]?.label ?? "";

  return (
    <header className="bg-white border-b border-slate-100 sticky top-0 z-30">
      {/* Barra principal */}
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="flex items-center justify-between h-14 gap-4">

          {/* Izquierda: breadcrumb + título */}
          <div className="flex items-center gap-2 min-w-0">
            {/* Breadcrumbs */}
            {crumbs.slice(0, -1).map((c, i) => (
              <span key={i} className="flex items-center gap-2 text-slate-400 shrink-0">
                {c.to ? (
                  <Link to={c.to}
                    className="text-xs font-medium text-slate-400 hover:text-slate-700 transition-colors">
                    {c.label}
                  </Link>
                ) : (
                  <span className="text-xs font-medium">{c.label}</span>
                )}
                <ChevronRight className="size-3 text-slate-300" />
              </span>
            ))}
            {/* Título activo */}
            <div className="flex items-center gap-2 min-w-0">
              {Icon && <Icon className="size-4 text-slate-500 shrink-0" />}
              <h1 className="text-base font-bold text-slate-900 truncate">{title}</h1>
            </div>
          </div>

          {/* Derecha: reloj + acciones */}
          <div className="flex items-center gap-2 shrink-0">
            {clock && <LiveClock className="hidden lg:block mr-2" />}
            {clock && actions.length > 0 && (
              <div className="hidden lg:block w-px h-6 bg-slate-200 mx-1" />
            )}

            {actions.map((a, i) => {
              const baseClass = "inline-flex items-center gap-1.5 text-sm font-medium transition-colors rounded-lg px-3 py-2 whitespace-nowrap";
              const variantClass =
                a.variant === "primary"
                  ? "bg-brand-600 text-white hover:bg-brand-700 shadow-sm"
                  : a.variant === "secondary"
                  ? "bg-slate-100 text-slate-700 hover:bg-slate-200"
                  : "text-slate-600 hover:text-slate-900 hover:bg-slate-50";

              const content = (
                <>
                  {a.icon && <a.icon className="size-4" />}
                  {a.label}
                  {a.badge !== undefined && a.badge > 0 && (
                    <span className="ml-0.5 px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-amber-400 text-white leading-none">
                      {a.badge}
                    </span>
                  )}
                </>
              );

              return a.to ? (
                <Link key={i} to={a.to} className={`${baseClass} ${variantClass}`}>
                  {content}
                </Link>
              ) : (
                <button key={i} onClick={a.onClick} className={`${baseClass} ${variantClass}`}>
                  {content}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </header>
  );
}
